import {
  ConfirmationDialog,
  PageSpinner,
  Section,
  useNotifications,
} from '@armhr/ui';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-mui';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';

import { useCompany } from '../../contexts/company.context';
import { useApi } from '../../hooks/useApi';
import { useApiData } from '../../hooks/useApiData';
import { useUser } from '../../hooks/useUser';
import { paths } from '../../utils/paths';

interface EditQuickHirePageProps {
  redirectPath?: string;
}

const EditQuickHirePage = ({ redirectPath }: EditQuickHirePageProps = {}) => {
  const { requestId } = useParams<{ requestId: string }>();
  const api = useApi();
  const { user } = useUser();
  const company = useCompany();
  const { showNotification } = useNotifications();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [isEmailConfirmOpen, setIsEmailConfirmOpen] = useState(false);

  const { data: newHireRequest, loading } = useApiData<NewHireRequest>((api) =>
    api.onboarding.getNewHireRequestById(requestId!)
  );

  const handleSubmit = async (values: NewHireRequestForm) => {
    setSubmitError(null);
    try {
      await api.onboarding.updateNewHireRequest(Number(requestId), {
        ...values,
        email: values.email.trim(),
      });
      showNotification({
        message: 'New hire request updated successfully.',
        severity: 'success',
      });
      navigate(redirectPath ?? paths.newHires);
    } catch (err: any) {
      const response = err?.response?.data;
      let errorMessage = 'There was a problem updating the new hire request.';

      if (Array.isArray(response?.detail)) {
        const { msg, loc } = response.detail[0] || {};
        const key = loc?.[1];
        errorMessage = key ? `${key}: ${msg}` : msg || errorMessage;
      } else if (typeof response?.detail === 'string') {
        errorMessage = response.detail;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setSubmitError(errorMessage);
      showNotification({
        message: errorMessage,
        severity: 'error',
      });
      window?.scrollTo(0, 0);
    }
  };

  const handleEmailClick = () => {
    setIsEmailConfirmOpen(true);
  };

  const handleConfirmEmail = async () => {
    if (!requestId) return;
    setEmailLoading(true);
    try {
      await api.onboarding.resendNewHireEmail(Number(requestId));
      showNotification({
        message: `Email sent successfully to ${newHireRequest?.email}`,
        severity: 'success',
      });
    } catch (error) {
      showNotification({
        message: 'There was a problem sending the email. Please try again.',
        severity: 'error',
      });
    } finally {
      setEmailLoading(false);
      setIsEmailConfirmOpen(false);
    }
  };

  if (loading) return <PageSpinner />;

  if (!newHireRequest) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">New hire request not found.</Alert>
      </Box>
    );
  }

  const initialValues: NewHireRequestForm = {
    first_name: newHireRequest.first_name || '',
    last_name: newHireRequest.last_name || '',
    email: newHireRequest.email || '',
  };

  return (
    <>
      <Helmet>
        <title>Edit Quick Hire | Armhr</title>
        <meta
          name="description"
          content="Edit a quick hire request with basic employee information."
        />
      </Helmet>
      <Formik initialValues={initialValues} onSubmit={handleSubmit}>
        {({ values, handleChange, isSubmitting }) => (
          <Form>
            <Box sx={{ display: 'flex', justifyContent: 'start', mb: 6 }}>
              <Box>
                <Typography variant="h2" mb={0.5}>
                  Edit quick hire
                </Typography>
                <Typography variant="body1">
                  Update the new hire's name and email address.
                </Typography>
              </Box>
              <Box sx={{ ml: 'auto' }}>
                <Button
                  color="secondary"
                  variant="outlined"
                  onClick={() => navigate(redirectPath ?? paths.newHires)}
                >
                  Back to new hires
                </Button>
              </Box>
            </Box>

            <Section
              title="New hire details"
              colSize={4}
              description={
                <>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    To verify their identity, the new hire is sent the following
                    details to confirm:
                  </Typography>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 2,
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body1">
                      Client ID: <strong>{company.id}</strong>
                    </Typography>
                    <Typography variant="body1">
                      Hiring ID:{' '}
                      <strong>
                        {(user as { id?: string } | null)?.id ?? ''}
                      </strong>
                    </Typography>
                  </Box>
                </>
              }
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {submitError && (
                  <Alert severity="error" onClose={() => setSubmitError(null)}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      Update Error
                    </Typography>
                    <Typography variant="body2">{submitError}</Typography>
                  </Alert>
                )}
                <Alert
                  sx={{
                    mb: 2,
                    py: 1.5,
                    lineHeight: '20px',
                    background: 'rgb(229, 246, 253)',
                  }}
                  severity="info"
                >
                  Saving will update the new hire's information. Use the "Send
                  email" button to resend the onboarding instructions email.
                </Alert>
                <Stack direction="row" spacing={2}>
                  <Field
                    name="first_name"
                    label="Legal first name"
                    fullWidth
                    required
                    value={values.first_name}
                    onChange={handleChange}
                    component={TextField}
                  />
                  <Field
                    name="last_name"
                    label="Legal last name"
                    fullWidth
                    required
                    value={values.last_name}
                    onChange={handleChange}
                    component={TextField}
                  />
                </Stack>
                <Field
                  name="email"
                  label={
                    user?.is_work_email_enabled
                      ? 'Work email address'
                      : 'Email address'
                  }
                  fullWidth
                  required
                  value={values.email}
                  onChange={handleChange}
                  component={TextField}
                />
                <Stack sx={{ ml: 'auto' }} direction="row" spacing={2}>
                  <Button
                    color="secondary"
                    variant="outlined"
                    onClick={() => navigate(redirectPath ?? paths.newHires)}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    color="primary"
                    size="large"
                    variant="outlined"
                    loading={emailLoading}
                    onClick={handleEmailClick}
                  >
                    Send email
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    size="large"
                    type="submit"
                    loading={isSubmitting}
                    variant="contained"
                  >
                    Save
                  </LoadingButton>
                </Stack>
              </Box>
            </Section>
          </Form>
        )}
      </Formik>

      <ConfirmationDialog
        open={isEmailConfirmOpen}
        onClose={() => setIsEmailConfirmOpen(false)}
        onConfirm={handleConfirmEmail}
        title="Confirm send email"
        message={
          <>
            This will send the prehire email to{' '}
            <strong>
              {newHireRequest?.first_name} {newHireRequest?.last_name}
            </strong>{' '}
            at <strong>{newHireRequest?.email}</strong>.
            <br />
            <br />
            Are you sure you want to send the email?
          </>
        }
      />
    </>
  );
};

export default EditQuickHirePage;
